import { endpoint } from '@library/endpoint'
import { canUseWindow, ENVIRONMENT_NAMES } from '@library/environment'
import { getEnvironment, isDev } from '@library/environment/isomorphic'

import routes from 'config/routes'
import { fetch, fetchRaw } from 'utils/fetch'
import type { UserOrder } from 'utils/types/order'

export function applyPromo(accessToken: string, promoCode: string) {
  return fetch(
    accessToken,
    `${endpoint('core')}/user/current/applyPromotionCode/${promoCode}`,
    {},
    'POST',
  )
}

export function fetchUserCredit(accessToken: string) {
  return fetch<{
    data: {
      balance: string
    }
  }>(accessToken, `${endpoint('core')}/user/current/balance`, {}, 'GET')
}

export function fetchReferralOffer(accessToken: string) {
  return fetch<{
    data: {
      code: string
      creditFormatted: string
      description: string
      details: string[]
      discountDuration: {
        value: number
        unit: string
        formatted: string
      }
      expiry: string | null
      firstBoxDiscountFormatted: string
      firstMonthDiscountFormatted: string
      // Set only on a boosted campaign, holding the discounts as they were before the boost.
      originalCampaignDetails: {
        creditFormatted: string
        firstBoxDiscountFormatted: string
        firstMonthDiscountFormatted: string
        secondBoxDiscountFormatted: string
      } | null
      title: string
      secondBoxDiscountFormatted: string
    }
  }>(accessToken, `${endpoint('core')}/user/current/raf-campaign-details`, {}, 'GET')
}

export function fetchPromo(accessToken: string) {
  return fetch(accessToken, `${endpoint('core')}${routes.core.userPromo}`, {}, 'GET')
}

/**
 * Resolve an auth-service identity id to Gousto's internal user id.
 *
 * `/internal/*` is core's internal surface: it is served by the core service directly and is
 * **not** exposed through the public API gateway, so it is only ever reachable server-side. The
 * guard makes that contract explicit — a client-side call fails loudly rather than attempting an
 * internal host the browser cannot reach.
 *
 * endpoint('core') resolves correctly for deployed environments (the `{env}-core` host) and the
 * `ENVIRONMENT=local` emulator (`localhost:4000`). The one case it can't cover is plain local dev
 * (LOCAL_DEV against staging), where it routes core through the `staging-api` gateway — which does
 * not expose `/internal/*` — so target the staging core host directly there.
 */
export function fromAuthUserId(authUserId: string) {
  if (canUseWindow()) {
    throw new Error(
      'fromAuthUserId is server-only: core /internal/user/fromauthid is not exposed via the API gateway',
    )
  }

  const base =
    isDev() && getEnvironment() !== ENVIRONMENT_NAMES.local
      ? 'http://staging-core.gousto.info'
      : endpoint('core')

  return fetch<{ data: { userId: number } }>(
    null,
    `${base}/internal/user/fromauthid/${authUserId}`,
    {},
    'GET',
  )
}

export function fetchUser(accessToken: string) {
  return fetch<{ data: { user: { id: string } } }>(
    accessToken,
    `${endpoint('core')}${routes.core.currentUser}`,
    {},
    'GET',
  )
}

export type ShippingAddress = {
  billingDefault: boolean
  companyname: string
  country: string
  deleted: boolean
  deliveryInstructions: string
  id: string
  line1: string
  line2: string
  line3: string
  name: string
  postcode: string
  premiumDelivery: boolean
  shippingDefault: boolean
  state: string
  town: string
  userId: number
}

export function fetchShippingAddresses(accessToken: string) {
  return fetch<{
    data: ShippingAddress[]
  }>(accessToken, `${endpoint('core')}${routes.core.userAddress}`, { type: 'shipping' }, 'GET')
}

export function fetchUserOrders(accessToken: string, reqData?: Record<string, unknown>) {
  return fetch<{
    data: UserOrder[]
  }>(accessToken, `${endpoint('core')}${routes.core.userOrders}`, reqData, 'GET')
}

export function reactivate(accessToken: string, reqData: { userId: string }) {
  return fetch(accessToken, `${endpoint('core')}/user/${reqData.userId}/restore`, {}, 'PUT')
}

export function verifyAge(accessToken: string, userId: string) {
  return fetch(accessToken, `${endpoint('core')}/user/${userId}`, { age_verified: 1 }, 'PUT')
}

export function checkDuplicateUser(reqData?: Record<string, unknown>) {
  return fetch(null, `${endpoint('core')}/user/check-duplicate`, reqData, 'POST')
}

export function referralDetails(accessToken: string, reqData?: Record<string, unknown>) {
  return fetch<{
    data: {
      referralCount: number
      referralCredit: number
    }
  }>(accessToken, `${endpoint('core')}/user/current/referralDetails`, reqData, 'GET')
}

export function addPaymentMethod(
  accessToken: string,
  reqData: Record<string, unknown>,
  userId: string,
) {
  return fetch(accessToken, `${endpoint('core')}/user/${userId}/paymentMethod`, reqData, 'PUT')
}

export function fetchUserAddresses(accessToken: string) {
  return fetch(accessToken, `${endpoint('core')}${routes.core.userAddress}`, {}, 'GET')
}
export function deleteMarketingSubscription(
  authUserId: string,
  marketingType: string,
  marketingUnsubscribeToken: string,
) {
  return fetchRaw(
    `${endpoint('core')}/user/${authUserId}/marketing/${marketingType}`,
    { marketing_unsubscribe_token: marketingUnsubscribeToken },
    { method: 'PUT', useOverwriteRequestMethod: false },
  )
}
